import React, {
    forwardRef,
    ForwardRefExoticComponent,
    RefAttributes,
    useEffect,
} from "react";
import { getComposeMessageComponentProps } from "util/message";
import { useComposeMessageQuery } from "../../generated/graphql";
import {
    getInsertableTemplates,
    getSignatureTemplates,
} from "../editor/upgrade-template";
import { ErrorMessage } from "../ui/error-message";
import { Spinner } from "../ui/spinner";
import { MessageComposeInternal } from "./message-compose";
import { PlacementMessageCompose, Props } from "./types";

const MessageComposePredeterminedRecipient: ForwardRefExoticComponent<
    Props & RefAttributes<PlacementMessageCompose>
> = forwardRef<PlacementMessageCompose, Props>((props, ref) => {
    const { entityId, requestSpecialMessageExperience } = props;

    const query = useComposeMessageQuery({
        entityId,
        specialMessageExperience: requestSpecialMessageExperience,
    });

    useEffect(() => {
        return () => {
            query.remove();
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (query.isLoading) {
        return <Spinner />;
    }
    if (query.isError) {
        return (
            <ErrorMessage component="MessageComposePredeterminedRecipient">
                <pre>{JSON.stringify(query.error, null, 2)}</pre>
            </ErrorMessage>
        );
    }
    if (!query.data) {
        return <Spinner />;
    }

    return (
        <MessageComposeInternal
            {...props}
            {...getComposeMessageComponentProps(query.data.composeMessage)}
            signatures={getSignatureTemplates(query.data.getTemplates.templates)}
            templates={getInsertableTemplates(query.data.getTemplates.templates)}
            hideSendControls
            hideRecipientFields
            ref={ref}
        />
    );
});

MessageComposePredeterminedRecipient.displayName = "MessageComposePredeterminedRecipient";
export { MessageComposePredeterminedRecipient };
